import { VatCode } from '@constants'
import React, { useEffect, useMemo, useState } from 'react'
import { Control, Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { get } from 'lodash'
import {
  FaBan,
  FaDollyFlatbed,
  FaEuroSign,
  FaGlobe,
  FaHammer,
  FaHome,
  FaMinusCircle
} from 'react-icons/fa'
import { InputWrapper } from '../Input'
import { VatIconWrapper, VatRateSelect, VatSelectWrapper, VatTypeSelect } from './VatSelect.styled'
import { useVATCodes } from '@root/query/hooks/useVATCodes'
import { FaCircle } from 'react-icons/fa6'

interface Props {
  id: string
  include: 'all' | 'sales' | 'purchase'
  vatCodeName: string
  vatRateName: string
  isInvalid: boolean
  control: Control<any>
  options: { value: number; label: string }[]
}

export const VatCodeIcons = {
  [VatCode.TAX_FREE]: FaBan,
  [VatCode.DOMESTIC_SALES_TAX]: FaHome,
  [VatCode.DOMESTIC_PURCHASE_TAX]: FaHome,
  [VatCode.DOMESTIC_ZERO_TAX_RATE]: FaMinusCircle,
  [VatCode.EU_SALE_OF_GOODS]: FaEuroSign,
  [VatCode.EU_SALE_OF_SERVICES]: FaEuroSign,
  [VatCode.EU_PURCHASE_OF_GOODS]: FaEuroSign,
  [VatCode.EU_PURCHASE_OF_SERVICES]: FaEuroSign,
  [VatCode.PURCHASE_OF_GOODS_OUTSIDE_EU]: FaDollyFlatbed,
  [VatCode.PURCHASE_OF_SERVICES_OUTSIDE_EU]: FaGlobe,
  [VatCode.SALE_OF_CONSTRUCTION_SERVICES]: FaHammer,
  [VatCode.PURCHASE_OF_CONSTRUCTION_SERVICES]: FaHammer,
  [VatCode.SALE_OF_GOODS_OUTSIDE_EU]: FaGlobe,
  [VatCode.SALE_OF_SERVICES_OUTSIDE_EU]: FaGlobe
}

export const VatSelect: React.FC<Props> = ({
  id,
  control,
  options,
  vatCodeName,
  vatRateName,
  include,
  isInvalid
}) => {
  const [t] = useTranslation()
  const [isFocused, setFocused] = useState(false)
  const [isHovered, setHovered] = useState(false)
  const { getValues, setValue } = useFormContext()

  const { data } = useVATCodes()
  const filtered = useMemo(
    () =>
      data?.filter(option => {
        switch (include) {
          case 'sales':
            return option?.tags?.includes('sales')
          case 'purchase':
            return option?.tags?.includes('purchase')
          case 'all':
            return true
        }
      }) ?? [],
    [data, include]
  )

  const vatCode = useWatch({ control, name: vatCodeName })
  const vatRate = useWatch({ control, name: vatRateName })
  const hideVatRate = [VatCode.TAX_FREE, VatCode.DOMESTIC_ZERO_TAX_RATE].includes(
    parseInt(vatCode || VatCode.TAX_FREE, 10)
  )

  // CASE: Options are changed dynamically
  // > Make sure selected value exists in the new options
  // > If not, select the first option automatically
  useEffect(() => {
    const oldVatRate = get(getValues(), vatRateName)
    const newVatRate = options.find(option => option.value === oldVatRate)?.value
    if (oldVatRate !== newVatRate) {
      setValue(vatRateName, newVatRate ?? options?.[0]?.value)
    }
  }, [JSON.stringify(options)])

  // Needed since if toggled from VAT code that needs a VAT rate to VAT code that doesn't need it,
  // the old value would still be used
  useEffect(() => {
    if (vatRate != null) {
      setValue(vatRateName, hideVatRate ? 0 : vatRate)
    }
  }, [hideVatRate])

  return (
    <VatSelectWrapper>
      <InputWrapper
        inputId={id}
        label={t('components.vatSelect.vat')}
        isFocused={isFocused}
        isInvalid={isInvalid}
      >
        <Controller
          control={control as any}
          name={vatCodeName}
          render={({ field: { ref, value, onChange } }) => {
            const Icon = VatCodeIcons[value] || FaCircle
            const selected = data?.find(vatCodeOption => vatCodeOption.value == value)
            const shouldRenderStandaloneSelectedOption =
              !!selected && !filtered.some(option => option.value === selected.value)

            return (
              <>
                <VatIconWrapper $isHovered={isHovered} key={value}>
                  {value ? <Icon size={16} /> : <FaBan />}
                </VatIconWrapper>

                <VatTypeSelect
                  data-test="vat-select"
                  ref={ref}
                  title={selected?.label}
                  style={{ opacity: 0 }}
                  value={value}
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                  onFocus={() => {
                    setFocused(true)
                  }}
                  onBlur={() => {
                    setFocused(false)
                  }}
                  onChange={ev => onChange?.(parseInt(ev.target.value, 10))}
                >
                  {shouldRenderStandaloneSelectedOption && (
                    <option key={`selected-${selected.value}`} value={selected.value}>
                      {selected.label}
                    </option>
                  )}
                  {filtered.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </VatTypeSelect>
              </>
            )
          }}
        />
        <Controller
          control={control as any}
          name={vatRateName}
          render={({ field: { ref, value, onChange } }) => {
            const hasMatchingOption = options.some(option => option.value === value)
            const shouldRenderStandAloneValue = !hideVatRate && value != null && !hasMatchingOption
            const standaloneLabel = shouldRenderStandAloneValue ? `${value} %` : undefined

            return (
              <VatRateSelect
                data-test="vat-amount"
                ref={ref}
                value={hideVatRate ? 0 : value}
                onChange={ev => onChange?.(parseFloat(ev.target.value))}
                className="percentage"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onFocus={() => {
                  setFocused(true)
                }}
                onBlur={() => {
                  setFocused(false)
                }}
                disabled={hideVatRate}
              >
                {shouldRenderStandAloneValue && (
                  <option key={`vat-rate-${value}`} value={value}>
                    {standaloneLabel}
                  </option>
                )}
                {options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </VatRateSelect>
            )
          }}
        />
      </InputWrapper>
    </VatSelectWrapper>
  )
}
